/**
 * Grupos, miembros y permisos de grupo.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { initialGroupState } from '../AdminPanel'; // Importar estado inicial
// Importar iconos SVG
import SettingsIcon from '../../assets/icons/settings.svg';
import EditIcon from '../../assets/icons/edit.svg';
import TrashIcon from '../../assets/icons/trash.svg';
import { studio, StudioIcon, SearchField, Field, FilterDock, FilterMeta, DockActionRow, cols } from './studioUi';
import { useAuth } from '../../AuthContext';
import ReportAccessPicker from './ReportAccessPicker';

const GroupManagement = ({
    groups, setGroups, selectedGroup, setSelectedGroup, 
    groupMembers, setGroupMembers, groupPermissions, setGroupPermissions,
    users, biblioteca, cargarTodo, styles, findUserName, userPermissions, fetchWithAuth, embedded = false
}) => {
    const { withBusy } = useAuth();
    // Define la URL base de tu API, usando la variable de entorno para producción
    const API_URL_INTERNAL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    const [nuevoGrupo, setNuevoGrupo] = useState(initialGroupState);
    const [editandoGrupoId, setEditandoGrupoId] = useState(null);
    const [showGroupForm, setShowGroupForm] = useState(false);
    const [filtroUsuarioDisponible, setFiltroUsuarioDisponible] = useState('');
    const [loadingGroupDetails, setLoadingGroupDetails] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [groupQuery, setGroupQuery] = useState('');

    // Function to refresh group permissions for the currently selected group
    const refreshGroupPermissions = useCallback(async (silent = false) => {
        if (selectedGroup) {
            if (!silent) setLoadingGroupDetails(true);
            try {
                const permissionsRes = await fetchWithAuth(`${API_URL_INTERNAL}/admin/groups/${selectedGroup.id}/permissions`);
                const permissionsData = await permissionsRes.json();
                if (permissionsData.success) {
                    setGroupPermissions(permissionsData.permissions || []);
                } else {
                    console.error("Error al recargar permisos del grupo:", permissionsData.error);
                }
            } catch (error) {
                console.error("Error de conexión al recargar permisos del grupo:", error);
            } finally {
                if (!silent) setLoadingGroupDetails(false);
            }
        }
    }, [selectedGroup, setGroupPermissions, fetchWithAuth, API_URL_INTERNAL]);

    // Efecto para cargar miembros y permisos del grupo seleccionado
    useEffect(() => {
        const fetchGroupDetails = async () => {
            if (selectedGroup) {
                setLoadingGroupDetails(true);
                try {
                    const membersRes = await fetchWithAuth(`${API_URL_INTERNAL}/admin/groups/${selectedGroup.id}/members`);
                    const membersData = await membersRes.json();
                    if (membersData.success) setGroupMembers(membersData.members || []);
                    await refreshGroupPermissions(); // Use the dedicated refresh function
                } catch (error) {
                    console.error("Error fetching group details:", error);
                }
            }
        };
        fetchGroupDetails();
    }, [selectedGroup, setGroupMembers, refreshGroupPermissions, API_URL_INTERNAL, fetchWithAuth]);

    const handleGroupSubmit = async (e) => {
        e.preventDefault();
        await withBusy(async () => {
        const url = editandoGrupoId ? `${API_URL_INTERNAL}/admin/groups/${editandoGrupoId}` : `${API_URL_INTERNAL}/admin/groups`;
        const method = editandoGrupoId ? 'PUT' : 'POST';
        const res = await fetchWithAuth(url, { // fetchWithAuth ya maneja el token
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(nuevoGrupo)
        });
        if (res.ok) {
            alert("Grupo guardado con éxito.");
            setNuevoGrupo(initialGroupState);
            setEditandoGrupoId(null);
            setShowGroupForm(false);
            cargarTodo();
        } else { // Manejo de errores mejorado
            let d = { error: "Error desconocido al guardar grupo." };
            try {
                d = await res.json();
            } catch (jsonError) {
                console.error("Failed to parse error response:", jsonError);
            }
            alert(d.error);
        }
        }, 'Guardando grupo…');
    };

    const handleAddMember = async (userId) => {
        if (!selectedGroup) return;
        const userToAdd = users.find(u => u.uid === userId);
        if (userToAdd) {
            setGroupMembers(prev => [...prev, { groupId: selectedGroup.id, userId: userToAdd.uid }]);
        }

        const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/groups/${selectedGroup.id}/members`, { // fetchWithAuth ya maneja el token
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId })
        });
        if (!res.ok) {
            let errorMsg = "Error desconocido al añadir miembro.";
            try {
                const errorData = await res.json();
                errorMsg = errorData.error || errorMsg;
            } catch (jsonError) {
                console.error("Failed to parse error response:", jsonError);
            }
            alert(errorMsg);
            // No es necesario cargarTodo si solo se revierte el estado local
        }
    };

    const handleRemoveMember = async (userId) => {
        if (!selectedGroup) return;
        const originalMembers = [...groupMembers];
        setGroupMembers(prev => prev.filter(m => m.userId !== userId));
        
        const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/groups/${selectedGroup.id}/members/${userId}`, { // fetchWithAuth ya maneja el token
            method: 'DELETE',
        });
        if (!res.ok) {
            let errorMsg = "Error desconocido al quitar miembro.";
            try {
                const errorData = await res.json();
                errorMsg = errorData.error || errorMsg;
            } catch (jsonError) {
                console.error("Failed to parse error response:", jsonError);
            }
            alert(errorMsg);
            setGroupMembers(originalMembers);
        }
    };

    const handleGroupPermissionToggle = async (report, isChecked) => {
        if (!selectedGroup) return false;
        const groupId = selectedGroup.id;
        if (isChecked) {
            const payload = {
                reporteId: report.id,
                cliente: report.cliente,
                clientId: report.clientId,
                proyecto: report.proyecto,
                projectId: report.projectId,
                reporteNombre: report.reporteNombre,
                dashboardUrl: report.dashboardUrl,
                accessType: 'view'
            };
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/groups/${groupId}/permissions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                await refreshGroupPermissions(true);
                return true;
            }
            const errorData = await res.json().catch(() => ({ error: "Error desconocido al asignar permiso." }));
            alert(`Error al asignar permiso al grupo: ${errorData.error}`);
            return false;
        }
        const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/groups/${groupId}/permissions/${report.id}`, {
            method: 'DELETE',
        });
        if (res.ok) {
            await refreshGroupPermissions(true);
            return true;
        }
        const errorData = await res.json().catch(() => ({ error: "Error desconocido al revocar permiso." }));
        alert(`Error al revocar permiso del grupo: ${errorData.error}`);
        return false;
    };

    const handleBulkGroupPermissions = async (reports, grant) => {
        if (!selectedGroup) return;
        for (const report of reports) {
            const groupId = selectedGroup.id;
            if (grant) {
                const payload = {
                    reporteId: report.id,
                    cliente: report.cliente,
                    clientId: report.clientId,
                    proyecto: report.proyecto,
                    projectId: report.projectId,
                    reporteNombre: report.reporteNombre,
                    dashboardUrl: report.dashboardUrl,
                    accessType: 'view'
                };
                await fetchWithAuth(`${API_URL_INTERNAL}/admin/groups/${groupId}/permissions`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                await fetchWithAuth(`${API_URL_INTERNAL}/admin/groups/${groupId}/permissions/${report.id}`, {
                    method: 'DELETE',
                });
            }
        }
        await refreshGroupPermissions(true);
    };

    const handleGroupAccessTypeChange = async (permissionId, newAccessType) => {
        if (!selectedGroup) return;
        const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/groups/${selectedGroup.id}/permissions/${permissionId}`, { // fetchWithAuth ya maneja el token
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accessType: newAccessType })
        });

        if (res.ok) {
            refreshGroupPermissions(true); // Refresh only group permissions
        } else {
            let errorMsg = "Error desconocido al actualizar el permiso del grupo.";
            try {
                const errorData = await res.json(); // Corrected variable name
                errorMsg = errorData.error || errorMsg;
            } catch (jsonError) { console.error("Failed to parse error response:", jsonError); } // Corrected variable name
            alert(errorMsg);
        }
    };

    if (selectedGroup) {
        const memberIds = new Set(groupMembers.map(m => m.userId));
        const availableUsers = users.filter(u =>
            (userPermissions.users_view_admin_pm || userPermissions.groups_manage_members ? true : (u.rol !== 'admin' && u.rol !== 'pm')) &&
            !memberIds.has(u.uid) &&
            (filtroUsuarioDisponible ? u.nombre.toLowerCase().includes(filtroUsuarioDisponible.toLowerCase()) : true)
        );

        const groupPermissionsMap = new Map();
        groupPermissions.forEach(p => {
            if (p.reporteId) groupPermissionsMap.set(p.reporteId, p);
            if (p.dashboardUrl) groupPermissionsMap.set(`url:${p.dashboardUrl}`, p);
        });
        const getGroupPermission = (report) => (
            groupPermissionsMap.get(report.id) || groupPermissionsMap.get(`url:${report.dashboardUrl}`) || null
        );

        const availableUsersByCompany = availableUsers.reduce((acc, user) => {
            const company = user.empresa || 'Sin Empresa';
            if (!acc[company]) acc[company] = [];
            acc[company].push(user);
            return acc;
        }, {});

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={studio.card}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px'}}>
                        <div>
                            <p style={studio.kicker}>Grupo</p>
                            <h3 style={{...studio.title, fontSize: '1.35rem'}}>{selectedGroup.name}</h3>
                            {selectedGroup.description && (
                                <p style={{...studio.subtitle, maxWidth: 'none'}}>{selectedGroup.description}</p>
                            )}
                        </div>
                        <button type="button" onClick={() => { setSelectedGroup(null); setFiltroUsuarioDisponible(''); }} style={studio.ghostBtn}>Volver a grupos</button>
                    </div>
                </div>
                <div style={{display: 'grid', gridTemplateColumns: cols('1fr 1fr'), gap: '16px', alignItems: 'start'}}>
                    <div style={studio.card}>
                        <h4 style={studio.formTitle}>Miembros ({groupMembers.length})</h4>
                        {loadingGroupDetails && <p style={studio.hint}>Cargando miembros…</p>}
                        <div style={{maxHeight: '60vh', overflowY: 'auto', paddingRight: '6px'}}>
                            {groupMembers.map(member => {
                                const user = users.find(u => u.uid === member.userId);
                                if (!user) return null;
                                return (
                                    <div key={user.uid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid #e8eaed' }}>
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.nombre} <span style={{color: '#5f6368'}}>({user.empresa})</span></span>
                                        <button type="button" onClick={() => handleRemoveMember(user.uid)} style={{...studio.dangerBtn, padding: '6px 10px', fontSize: '0.8rem'}}>Quitar</button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div style={studio.card}>
                        <h4 style={studio.formTitle}>Añadir personas</h4>
                        <SearchField
                            value={filtroUsuarioDisponible}
                            onChange={setFiltroUsuarioDisponible}
                            placeholder="Buscar para añadir…"
                            style={{ marginBottom: 14 }}
                        />
                        <div style={{maxHeight: '50vh', overflowY: 'auto', paddingRight: '6px'}}>
                            {Object.keys(availableUsersByCompany).sort().map(company => (
                                <div key={company}>
                                    <details style={{marginBottom: '6px'}}>
                                        <summary style={styles.companyHeader}>{company} ({availableUsersByCompany[company].length})</summary>
                                        {availableUsersByCompany[company].map(user => (
                                            <div key={user.uid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '8px 0 8px 12px', borderBottom: '1px solid #f1f3f4' }}>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.nombre} <span style={{color: '#777', fontSize: '0.85rem'}}>({user.email})</span></span>
                                                <button type="button" onClick={() => handleAddMember(user.uid)} style={{...studio.ghostBtn, padding: '6px 10px', fontSize: '0.8rem'}}>Añadir</button>
                                            </div>
                                        ))}
                                    </details>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div style={studio.card}>
                    {loadingGroupDetails && <p style={studio.hint}>Cargando permisos…</p>}
                    <h4 style={studio.formTitle}>Reportes del grupo</h4>
                    <p style={{...studio.hint, marginBottom: '14px'}}>Los reportes marcados serán visibles para todos los miembros.</p>
                    <ReportAccessPicker
                        reports={biblioteca}
                        getPermission={getGroupPermission}
                        onToggle={handleGroupPermissionToggle}
                        onBulkToggle={handleBulkGroupPermissions}
                        onAccessTypeChange={handleGroupAccessTypeChange}
                        canAssign={!!userPermissions.groups_manage_permissions}
                        canRevoke={!!userPermissions.groups_manage_permissions}
                        canChangeType={!!userPermissions.groups_manage_permissions}
                        busy={loadingGroupDetails}
                    />
                </div>
            </div>
        );
    }

    const gruposFiltrados = (groups || []).filter((g) => {
        const term = groupQuery.trim().toLowerCase();
        if (!term) return true;
        return (g.name || '').toLowerCase().includes(term) || (g.description || '').toLowerCase().includes(term);
    });

    return (
        <div>
             <div style={studio.card}>
                {!embedded && <h3 style={{ margin: '0 0 16px' }}>Gestión de Grupos</h3>}
                {showGroupForm && (
                    <div style={studio.formPanel}>
                        <h3 style={studio.formTitle}>{editandoGrupoId ? 'Editar grupo' : 'Nuevo grupo'}</h3>
                        <form onSubmit={handleGroupSubmit} style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                            <Field label="Nombre">
                                <input placeholder="Nombre del grupo" value={nuevoGrupo.name} onChange={e => setNuevoGrupo({...nuevoGrupo, name: e.target.value})} style={studio.control} required />
                            </Field>
                            <Field label="Descripción">
                                <textarea placeholder="Descripción (opcional)" value={nuevoGrupo.description} onChange={e => setNuevoGrupo({...nuevoGrupo, description: e.target.value})} style={{...studio.control, height: '80px', resize: 'vertical'}} />
                            </Field>
                            <div style={{display: 'flex', gap: '10px'}}>
                                <button type="submit" style={studio.primaryBtn}>{editandoGrupoId ? 'Actualizar' : 'Crear grupo'}</button>
                                <button type="button" onClick={() => { setEditandoGrupoId(null); setNuevoGrupo(initialGroupState); setShowGroupForm(false); }} style={studio.ghostBtn}>Cancelar</button>
                            </div>
                        </form>
                    </div>
                )}
                <FilterDock>
                    <DockActionRow
                        action={!showGroupForm && userPermissions.groups_create ? (
                            <button type="button" onClick={() => setShowGroupForm(true)} style={studio.primaryBtn}>Nuevo grupo</button>
                        ) : null}
                    >
                        <SearchField value={groupQuery} onChange={setGroupQuery} placeholder="Buscar grupo…" style={{ marginBottom: 0 }} />
                    </DockActionRow>
                    <FilterMeta
                        summary={`${gruposFiltrados.length} ${gruposFiltrados.length === 1 ? 'grupo' : 'grupos'}`}
                        chips={groupQuery ? [{ key: 'q', label: `“${groupQuery}”`, onClear: () => setGroupQuery('') }] : []}
                        onClearAll={groupQuery ? () => setGroupQuery('') : undefined}
                    />
                </FilterDock>
                {gruposFiltrados.length === 0 && (
                    <div style={studio.empty}>{groups.length === 0 ? 'Aún no hay grupos.' : 'Ningún grupo coincide con la búsqueda.'}</div>
                )}
                {gruposFiltrados.length > 0 && (
                <table style={studio.table}>
                    <thead><tr>
                        <th style={studio.th}>Grupo</th>
                        <th style={studio.th}>Descripción</th>
                        <th style={{...studio.th, width: '140px'}}></th>
                    </tr></thead>
                    <tbody>{gruposFiltrados.map(g => (
                        <tr
                            key={g.id}
                            style={hoveredRow === g.id ? studio.trHover : undefined}
                            onMouseEnter={() => setHoveredRow(g.id)}
                            onMouseLeave={() => setHoveredRow(null)}
                        >
                            <td style={{...studio.td, fontWeight: 600}}>{g.name}</td>
                            <td style={{...studio.td, color: '#5f6368'}}>{g.description || '—'}</td>
                            <td style={{...studio.td, whiteSpace: 'nowrap'}}>
                                {userPermissions.groups_manage_members && userPermissions.groups_manage_permissions && (
                                    <button title="Gestionar grupo" onClick={() => setSelectedGroup(g)} style={studio.iconBtn}>
                                        <StudioIcon src={SettingsIcon} alt="Gestionar" />
                                    </button>
                                )}
                                {userPermissions.groups_edit && (
                                    <button title="Editar grupo" onClick={() => { setEditandoGrupoId(g.id); setNuevoGrupo(g); setShowGroupForm(true); }} style={studio.iconBtn}>
                                        <StudioIcon src={EditIcon} alt="Editar" />
                                    </button>
                                )}
                                {userPermissions.groups_delete && (
                                    <button title="Borrar grupo" onClick={async () => {
                                        if(window.confirm(`¿Borrar el grupo "${g.name}"?`)){
                                            await fetchWithAuth(`${API_URL_INTERNAL}/admin/groups/${g.id}`,{method:'DELETE'});
                                            cargarTodo();
                                        }
                                    }} style={studio.iconBtnDanger}>
                                        <StudioIcon src={TrashIcon} alt="Eliminar" />
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}</tbody>
                </table>
                )}
            </div>
        </div>
    );
};

// Declaramos el estilo base de las imágenes a nivel de fallback local
const iconImageStyle = {
    verticalAlign: 'middle', 
    filter: 'invert(13%) sepia(90%) saturate(6000%) hue-rotate(330deg) brightness(90%) contrast(100%)'
};

GroupManagement.defaultProps = {
    styles: {
        iconImage: iconImageStyle,
    }
};

export default GroupManagement;